/**
 * "+ New QC" — which QC? (prototype v13 `newQc`, 2026-09-28)
 *
 *   Reference  read a reference slide (Daily QC) — opens the slide picker
 *   Intra dup  re-read one of YOUR OWN sample layers — the SAME analyst
 *   Inter dup  a blind re-read of ANOTHER analyst's batch, once every sample
 *              layer of it is entered ("Pending Interdup") — a DIFFERENT analyst
 *              (owner rule, 2026-09-29)
 *
 * The choices offered are the ones plmWorkflow says the user may start; the
 * server re-checks every rule (dbo.AI_Plm_AddReRead_SP, PlmController), so a
 * stale list can only produce a refusal, never a wrong re-read.
 */
import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import {
  addPlmReRead, fetchPlmBatches, fetchPlmLayers, type PlmBatchHeader, type PlmLayerRow,
} from './plm.api';
import { dupLabel, interCandidates, interdupBlock, intraCandidates } from './plmWorkflow';

type Kind = 'Reference' | 'Intra' | 'Inter';

const same = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

interface Props {
  open: boolean;
  onClose: () => void;
  /** The signed-in user's Employee Oid. */
  me: string | null;
  /** Start on this batch (from inside a batch); otherwise the user picks one. */
  batchOid?: string | null;
  /** A re-read was created — open it (Enter mode, on the new layer). */
  onCreated: (batchOid: string, sequenceOid: string) => void;
  /** Reference picked — open the Daily QC slide picker. */
  onReference?: () => void;
  /** The QC dup switch (lookups.dupSubmitButtons, 2026-10-17). False = the SIMPLE flow:
   *  there is no Inter-dup to "start" — the Inter-dup choice OPENS the waiting dup and the
   *  analyst's first save of it makes it theirs (owner 2026-10-17, G5 parity:
   *  PlmV2ListControllers.NewQc_Execute). Default: today's flow. */
  dupSteps?: boolean;
}

export default function PlmNewQcDialog({
  open, onClose, me, batchOid, onCreated, onReference, dupSteps = true,
}: Props) {
  const confirm = useConfirm();
  const notify = useNotify();
  // Reference is the prototype's default choice (the daily read comes first).
  const [kind, setKind] = useState<Kind>(onReference ? 'Reference' : 'Intra');
  const [batches, setBatches] = useState<PlmBatchHeader[]>([]);
  const [intraBatch, setIntraBatch] = useState<string>('');
  const [interBatch, setInterBatch] = useState<string>('');
  const [layersByBatch, setLayersByBatch] = useState<Record<string, PlmLayerRow[]>>({});
  // Inter-dup: the open batches someone else analysed with an Inter-dup waiting (G5 InterBatches).
  const [interBatches, setInterBatches] = useState<PlmBatchHeader[]>([]);
  const [intraPick, setIntraPick] = useState('');
  const [interPick, setInterPick] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    void fetchPlmBatches().then(all => {
      const openOnes = all.filter(b => b.editable);
      setBatches(openOnes);
      // Intra-dup = the SAME analyst: batches the user analysed. Inter-dup = a
      // DIFFERENT analyst: batches someone else analysed, where an Inter-dup the
      // original analyst added is waiting to be read (owner rules 2026-09-29,
      // 2026-10-07).
      const mineFirst = openOnes.find(b => b.batchOid === batchOid && same(b.firstAnalystOid, me))
        ?? openOnes.find(b => same(b.firstAnalystOid, me));
      setIntraBatch(mineFirst?.batchOid ?? '');
      // Inter-dup: the open batches someone ELSE analysed that hold an Inter-dup nobody
      // has started (owner 2026-10-17, G5 parity — read from their layers, not the stage).
      const others = openOnes.filter(b => !same(b.firstAnalystOid, me));
      void Promise.all(others.map(b => fetchPlmLayers(b.batchOid).then(rows => [b.batchOid, rows] as const)))
        .then(loaded => {
          const byBatch = Object.fromEntries(loaded);
          setLayersByBatch(m => ({ ...m, ...byBatch }));
          const waiting = others.filter(b => interCandidates(byBatch[b.batchOid] ?? [], me).length > 0);
          setInterBatches(waiting);
          setInterBatch((waiting.find(b => b.batchOid === batchOid) ?? waiting[0])?.batchOid ?? '');
        })
        .catch(() => setInterBatches([]));
    });
  }, [open, batchOid, me]);

  // Load a batch's layers once, when it is picked.
  useEffect(() => {
    for (const b of [intraBatch, interBatch]) {
      if (b && !layersByBatch[b]) {
        void fetchPlmLayers(b).then(rows => setLayersByBatch(m => ({ ...m, [b]: rows })));
      }
    }
  }, [intraBatch, interBatch, layersByBatch]);

  const intraBatches = useMemo(() => batches.filter(b => same(b.firstAnalystOid, me)), [batches, me]);
  // Why a list is empty — so an empty dropdown explains itself instead of
  // looking like a load failure (user report 2026-09-29). G5's words (Hint).
  const intraEmpty = batches.length === 0 ? 'no PLM batch is open for result entry'
    : 'you have not analyzed a layer in any open batch';
  const interEmpty = batches.length === 0 ? 'no PLM batch is open for result entry'
    : 'no Inter-dup is waiting for you: the original analyst adds it on the PLM entry page, and it must be read by another analyst';
  const intraSamples = useMemo(() => intraCandidates(layersByBatch[intraBatch] ?? [], me), [layersByBatch, intraBatch, me]);
  const interSamples = useMemo(() => interCandidates(layersByBatch[interBatch] ?? [], me), [layersByBatch, interBatch, me]);
  const interWhy = interBatch ? interdupBlock(layersByBatch[interBatch] ?? [], me) : null;

  useEffect(() => { setIntraPick(intraSamples[0]?.sequenceOid ?? ''); }, [intraSamples]);
  useEffect(() => { setInterPick(interSamples[0]?.sequenceOid ?? ''); }, [interSamples]);

  const pick = kind === 'Intra' ? intraPick : kind === 'Inter' ? interPick : '';
  const pickBatch = kind === 'Intra' ? intraBatch : interBatch;
  const pickLayer = (layersByBatch[pickBatch] ?? []).find(l => l.sequenceOid === pick);

  const start = async () => {
    if (kind === 'Reference') {
      // The slide is picked on the Daily QC screen (Last Run / Days Ago).
      onClose();
      onReference?.();
      return;
    }
    if (!pick || !pickLayer) return;
    if (kind === 'Inter' && !dupSteps) {
      // The SIMPLE flow: nothing is started here — the Inter-dup opens, and the analyst's
      // first save of it makes it theirs (owner 2026-10-17, G5 parity).
      notify.info(`${dupLabel(pickLayer.sampleL)} opened — enter and save it to read it (blind).`);
      onClose();
      onCreated(pickBatch, pick);
      return;
    }
    const ok = await confirm.confirm({
      title: kind === 'Inter' ? 'Start Inter-dup' : dupSteps ? 'Start Intra-dup' : 'Add Intra-dup',
      message: kind === 'Inter'
        ? `Start reading ${dupLabel(pickLayer.sampleL)}? You read it blind — the original stays hidden from you until you submit your read.`
        : `Read your layer ${pickLayer.sampleL} a second time as its Intra-dup?`,
      confirmLabel: dupSteps ? 'Start' : 'Add',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await addPlmReRead(pick, kind);
      notify.success(kind === 'Inter'
        ? `${dupLabel(pickLayer.sampleL)} started — blind.`
        : dupSteps ? `Intra-dup started on ${pickLayer.sampleL}.` : `Intra-dup added on ${pickLayer.sampleL}.`);
      onClose();
      onCreated(pickBatch, res.sequenceOid);
    } finally {
      // A refusal (e.g. "You performed the original analysis — the Interdup must
      // be done by another analyst.") is shown by the shared API client.
      setBusy(false);
    }
  };

  const canStart = !busy && (kind === 'Reference' ? !!onReference : !!pick);

  return (
    <AppDialog open={open} onClose={onClose} title="New QC" icon="ti-flask" width={560}
      className="plm-newqc"
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => { void start(); }} disabled={!canStart}>Continue</button>
      </>}>
      <p className="plm-newqc__q">Which QC?</p>
      <div className="plm-newqc__opts" role="radiogroup" aria-label="QC type">
        <label className={`plm-newqc__opt${onReference ? '' : ' is-disabled'}`}>
          <input type="radio" name="plm-newqc" value="Reference" checked={kind === 'Reference'}
            disabled={!onReference} onChange={() => setKind('Reference')} />
          <span><span className="plm-qctag is-ref">REF</span>Reference — pick a reference slide (Daily QC)</span>
        </label>

        <label className="plm-newqc__opt">
          <input type="radio" name="plm-newqc" value="Intra" checked={kind === 'Intra'}
            onChange={() => setKind('Intra')} />
          <span><span className="plm-qctag is-intra">INTRA</span>Intra-dup — read one of your own samples a second time</span>
        </label>
        <div className="plm-newqc__sub">
          QC Batch
          <select className="plm-input" value={intraBatch} disabled={kind !== 'Intra'}
            onChange={e => setIntraBatch(e.target.value)} aria-label="Intra-dup QC batch">
            {intraBatches.length === 0 && <option value="">— none ({intraEmpty}) —</option>}
            {intraBatches.map(b => <option key={b.batchOid} value={b.batchOid}>{b.analyticalBatchId}</option>)}
          </select>
          Sample
          <select className="plm-input" value={intraPick} disabled={kind !== 'Intra'}
            onChange={e => setIntraPick(e.target.value)} aria-label="Intra-dup sample">
            {intraSamples.length === 0 && <option value="">
              {intraBatch ? '— none (every layer you analyzed has its Intra-dup) —' : '— none —'}</option>}
            {intraSamples.map(l => <option key={l.sequenceOid} value={l.sequenceOid}>{l.sampleL}</option>)}
          </select>
        </div>

        {/* Offered in both flows (owner 2026-10-17, G5 parity); the SIMPLE flow opens it. */}
        <label className="plm-newqc__opt">
          <input type="radio" name="plm-newqc" value="Inter" checked={kind === 'Inter'}
            onChange={() => setKind('Inter')} />
          <span><span className="plm-qctag is-inter">INTER</span>Inter-dup — read a dup another analyst added on their sample (blind)</span>
        </label>
        <div className="plm-newqc__sub">
          QC Batch
          <select className="plm-input" value={interBatch} disabled={kind !== 'Inter'}
            onChange={e => setInterBatch(e.target.value)} aria-label="Inter-dup QC batch">
            {interBatches.length === 0 && <option value="">— none ({interEmpty}) —</option>}
            {interBatches.map(b => <option key={b.batchOid} value={b.batchOid}>{b.analyticalBatchId}</option>)}
          </select>
          Sample ID
          <select className="plm-input" value={interPick} disabled={kind !== 'Inter'}
            onChange={e => setInterPick(e.target.value)} aria-label="Inter-dup sample">
            {interSamples.length === 0 && <option value="">
              {interBatch && !interWhy
                ? '— none (no Inter-dup is waiting: the original analyst adds it on the PLM entry page) —'
                : '— none —'}</option>}
            {interSamples.map(l => <option key={l.sequenceOid} value={l.sequenceOid}>{l.sampleL}</option>)}
          </select>
        </div>
        {kind === 'Inter' && interWhy && <p className="plm-newqc__why">{interWhy}</p>}
      </div>
    </AppDialog>
  );
}
